import React, { useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";

export default function CTCTemplates() {
  const [templates, setTemplates] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [current, setCurrent] = useState({
    id: null,
    name: "",
    description: "",
    components: [],
  });

  const emptyComponent = {
    name: "",
    type: "earning",
    calcType: "fixed",
    amount: "",
    formula: "",
  };

  function openNewTemplate() {
    setCurrent({
      id: null,
      name: "",
      description: "",
      components: [],
    });
    setEditMode(false);
    setDrawerOpen(true);
  }

  function openEdit(tpl) {
    setCurrent(tpl);
    setEditMode(true);
    setDrawerOpen(true);
  }

  function addComponent() {
    setCurrent((p) => ({
      ...p,
      components: [...p.components, { ...emptyComponent }],
    }));
  }

  function updateComponent(i, field, value) {
    const updated = [...current.components];
    updated[i][field] = value;
    setCurrent((p) => ({ ...p, components: updated }));
  }

  function deleteComponent(i) {
    const updated = current.components.filter((_, idx) => idx !== i);
    setCurrent((p) => ({ ...p, components: updated }));
  }

  function saveTemplate() {
    if (!current.name.trim()) return alert("Template name required");

    if (editMode) {
      setTemplates((p) =>
        p.map((t) => (t.id === current.id ? current : t))
      );
    } else {
      setTemplates((p) => [
        ...p,
        { ...current, id: Date.now() },
      ]);
    }

    setDrawerOpen(false);
  }

  function deleteTemplate(id) {
    if (!window.confirm("Delete this CTC template?")) return;
    setTemplates((p) => p.filter((t) => t.id !== id));
  }

  return (
    <div className="p-6 md:p-8 bg-gray-50 min-h-screen font-['Poppins','Roboto',sans-serif]">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold">CTC Templates</h1>
            <p className="text-gray-500 text-sm">
              Create reusable salary structure templates for faster employee onboarding.
            </p>
          </div>

          <button
            onClick={openNewTemplate}
            className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded shadow hover:bg-teal-700"
          >
            <Plus size={18} /> New Template
          </button>
        </div>

        {/* TEMPLATES LIST */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {templates.length === 0 ? (
            <div className="col-span-2 text-center text-gray-500 italic">
              No templates created yet.
            </div>
          ) : (
            templates.map((tpl) => (
              <div key={tpl.id} className="bg-white p-6 rounded-xl shadow border">
                <h2 className="font-semibold text-lg">{tpl.name}</h2>
                <p className="text-sm text-gray-500">{tpl.description}</p>

                <div className="mt-4 border-t pt-3">
                  <p className="text-sm font-medium text-gray-600">Components:</p>
                  {tpl.components.slice(0, 3).map((c, i) => (
                    <p key={i} className="text-xs text-gray-500">
                      • {c.name} ({c.type})
                    </p>
                  ))}
                  {tpl.components.length > 3 && (
                    <p className="text-xs text-gray-400 mt-1">
                      +{tpl.components.length - 3} more…
                    </p>
                  )}
                </div>

                <div className="flex gap-3 mt-5">
                  <button
                    onClick={() => openEdit(tpl)}
                    className="flex items-center gap-1 text-blue-600 text-sm border px-3 py-1 rounded hover:bg-blue-50"
                  >
                    <Edit size={16} /> Edit
                  </button>
                  <button
                    onClick={() => deleteTemplate(tpl.id)}
                    className="flex items-center gap-1 text-red-600 text-sm border px-3 py-1 rounded hover:bg-red-50"
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* DRAWER */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="fixed right-0 top-0 w-full md:w-2/5 h-full bg-white shadow-xl z-50 overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold">
                {editMode ? "Edit Template" : "New CTC Template"}
              </h2>
              <button onClick={() => setDrawerOpen(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* TEMPLATE NAME */}
              <div>
                <label className="block text-sm font-medium">Template Name</label>
                <input
                  type="text"
                  value={current.name}
                  onChange={(e) =>
                    setCurrent((p) => ({ ...p, name: e.target.value }))
                  }
                  className="w-full mt-1 border rounded p-2"
                />
              </div>

              {/* DESCRIPTION */}
              <div>
                <label className="block text-sm font-medium">Description</label>
                <input
                  type="text"
                  value={current.description}
                  onChange={(e) =>
                    setCurrent((p) => ({ ...p, description: e.target.value }))
                  }
                  className="w-full mt-1 border rounded p-2"
                />
              </div>

              {/* COMPONENTS */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">Components</h3>
                  <button
                    onClick={addComponent}
                    className="flex items-center gap-1 text-sm border rounded px-3 py-1"
                  >
                    <Plus size={14} /> Add Component
                  </button>
                </div>

                {current.components.length === 0 && (
                  <p className="text-gray-400 text-sm">No components added.</p>
                )}

                <div className="space-y-4">
                  {current.components.map((c, i) => (
                    <div key={i} className="border rounded p-4 bg-gray-50">
                      <div className="flex justify-between">
                        <h4 className="font-medium text-sm">Component {i + 1}</h4>
                        <button
                          onClick={() => deleteComponent(i)}
                          className="text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Component name */}
                      <div className="mt-3">
                        <label className="text-sm">Name</label>
                        <input
                          type="text"
                          value={c.name}
                          onChange={(e) =>
                            updateComponent(i, "name", e.target.value)
                          }
                          className="w-full border rounded p-2 mt-1"
                        />
                      </div>

                      {/* Component type */}
                      <div className="mt-3">
                        <label className="text-sm">Type</label>
                        <select
                          value={c.type}
                          onChange={(e) =>
                            updateComponent(i, "type", e.target.value)
                          }
                          className="w-full border rounded p-2 mt-1"
                        >
                          <option value="earning">Earning</option>
                          <option value="deduction">Deduction</option>
                          <option value="employer">Employer Contribution</option>
                          <option value="reimbursement">Reimbursement</option>
                        </select>
                      </div>

                      {/* Calculation type */}
                      <div className="mt-3">
                        <label className="text-sm">Calculation Type</label>
                        <select
                          value={c.calcType}
                          onChange={(e) =>
                            updateComponent(i, "calcType", e.target.value)
                          }
                          className="w-full border rounded p-2 mt-1"
                        >
                          <option value="fixed">Fixed Amount</option>
                          <option value="formula">Formula</option>
                        </select>
                      </div>

                      {/* Amount or Formula Field */}
                      {c.calcType === "fixed" ? (
                        <div className="mt-3">
                          <label className="text-sm">Amount (₹)</label>
                          <input
                            type="number"
                            value={c.amount}
                            onChange={(e) =>
                              updateComponent(i, "amount", e.target.value)
                            }
                            className="w-full border rounded p-2 mt-1"
                          />
                        </div>
                      ) : (
                        <div className="mt-3">
                          <label className="text-sm">Formula</label>
                          <input
                            type="text"
                            value={c.formula}
                            onChange={(e) =>
                              updateComponent(i, "formula", e.target.value)
                            }
                            placeholder="E.g. basic * 0.12"
                            className="w-full border rounded p-2 mt-1"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={saveTemplate}
                className="w-full bg-teal-600 text-white py-2 rounded shadow hover:bg-teal-700"
              >
                {editMode ? "Update Template" : "Create Template"}
              </button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
